import { defineComponent } from "vue";
import Card from "@/components/primitives/cards/Cards";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

/**
 * KnowledgeSkeleton - Loading skeleton for Knowledge base page
 *
 * Displays a complex skeleton with:
 * - Header with title and action buttons
 * - Search bar and controls row
 * - Sticky-column table structure
 * - Pagination controls
 *
 * @example
 * ```tsx
 * <KnowledgeSkeleton rows={10} columns={5} />
 * ```
 */
export default defineComponent({
  name: "KnowledgeSkeleton",
  props: {
    /**
     * Number of action buttons in header
     * @default 6
     */
    buttonCount: p.number(6),

    /**
     * Number of table rows
     * @default 10
     */
    rows: p.number(10),

    /**
     * Number of table columns (excluding checkbox and actions columns)
     * @default 4
     */
    columns: p.number(4),

    /**
     * Whether to show pagination controls
     * @default true
     */
    showPagination: p.boolean(true),
  },
  setup(props) {
    return () => (
      <Card>
        <Card.Header>
          {/* Header: Title + Action Buttons */}
          <div class="flex flex-wrap justify-between gap-3">
            <div class="flex flex-col gap-y-1.5">
              <Skeleton class="h-7 w-48" /> {/* Title */}
              <Skeleton class="h-4 w-64" /> {/* Description */}
            </div>
            <div class="flex flex-wrap gap-2">
              {Array.from({ length: props.buttonCount }).map((_, i) => (
                <Skeleton key={i} class="h-9 w-24" /> /* Button skeleton */
              ))}
            </div>
          </div>
        </Card.Header>

        <Card.Content>
          {/* Search Bar + Controls */}
          <div class="mb-2 flex items-center justify-between gap-2">
            <div class="flex gap-3">
              <Skeleton class="h-9 w-72" /> {/* Search input */}
            </div>
            <Skeleton class="h-11 w-32" /> {/* Auto sync switch */}
          </div>

          {/* Table */}
          <div class="overflow-hidden rounded border">
            {/* Table Header */}
            <div class="flex border-b bg-surface-2">
              <div class="border-r p-3">
                <Skeleton class="h-4 w-4" /> {/* Checkbox */}
              </div>
              {Array.from({ length: props.columns }).map((_, i) => (
                <div key={i} class="flex-1 border-r p-3 last:border-r-0">
                  <Skeleton class="h-4 w-24" /> {/* Column header */}
                </div>
              ))}
              <div class="w-16 p-3">
                <Skeleton class="h-4 w-4" /> {/* Add column button */}
              </div>
            </div>

            {/* Table Rows */}
            {Array.from({ length: props.rows }).map((_, rowIndex) => (
              <div key={rowIndex} class="flex border-b last:border-b-0 hover:bg-card/50">
                <div class="border-r p-3">
                  <Skeleton class="h-4 w-4" /> {/* Row checkbox */}
                </div>
                {Array.from({ length: props.columns }).map((_, colIndex) => (
                  <div key={colIndex} class="flex-1 border-r p-3 last:border-r-0">
                    <Skeleton class="h-4 w-full" /> {/* Cell content */}
                  </div>
                ))}
                <div class="w-16 p-3">
                  <Skeleton class="h-4 w-8" /> {/* Row actions */}
                </div>
              </div>
            ))}

            {/* Add Row Button */}
            <div class="border-t p-2">
              <Skeleton class="h-6 w-24" />
            </div>
          </div>

          {/* Pagination */}
          {props.showPagination && (
            <div class="mt-3 flex items-center justify-between">
              <Skeleton class="h-4 w-48" /> {/* Showing X to Y of Z results */}
              <div class="flex gap-2">
                {/* Page buttons */}
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} class="h-8 w-8 rounded" />
                ))}
              </div>
              <div class="flex items-center gap-2">
                <Skeleton class="h-4 w-20" /> {/* Items per page label */}
                <Skeleton class="h-8 w-16 rounded" /> {/* Page size select */}
              </div>
            </div>
          )}
        </Card.Content>
      </Card>
    );
  },
});
